/**
 * BAN の異議申し立ての一覧と回答（Players の画面の上・2026-09-27 appeal-tools・Quality SEC-HIDE-03）。
 * base = `…/environments/:env`（envPath）。API は GET {base}/appeals?status=open・POST {base}/appeals/:appealId/resolve
 * （apps/server/src/modules/integrity/routes.ts。機能の切り替えの外 = 常に開いている）。
 *
 * WHY ここに置くか: founder の決定（MASTER-PLAN §1c）は「プレイヤーの BAN は残す」。BAN を掛ける画面（PlayerDrawer）の近くに
 *   受けた異議が無いと、誤った BAN が取り消されない（以前は REST を直接叩くしかなかった）。
 * WHY 未回答だけを出すか: 回答済みは監査ログに残る（player.appeal.resolve）。ここは「今やること」の置き場にする。
 * WHY 未回答が 0 件でも欄を出すか: 異議が届く場所を、BAN をしたクリエイターが先に知っておけるように（1 行だけ）。
 * プレイヤーの名前は一覧の API に無い（ID だけ）。押すとプレイヤーの詳細（PlayerDrawer）を開く。
 */
import { useState } from 'react';
import { api, ApiError, type AppealRow } from '../api';
import { resolveBody, sanctionKindKey, APPEAL_RESPONSE_MAX } from '../appealsLogic';
import { usePagedResource } from '../hooks';
import { LoadMore } from './LoadMore';
import { errorText, useT } from '../i18n';
import { ago, ConfirmDialog, ErrorState, fullDate, shortId, Spinner } from './ui';

export function AppealsPanel({ base, onOpenPlayer, onResolved }: { base: string; onOpenPlayer: (playerId: string) => void; onResolved: () => void }) {
  const { t, tr } = useT();
  // 50 件ずつ「さらに読み込む」で新しい申し立てへ（2026-10-04 console/limits-ui。以前は 200 件で切れて、それより後の申し立てに
  // 返事ができなかった）。古い順 = 長く待たせている人から
  const list = usePagedResource<{ appeals: AppealRow[]; nextCursor?: string | null }, 'appeals'>(`${base}/appeals?status=open&limit=50`, 'appeals');
  const [target, setTarget] = useState<{ appeal: AppealRow; accept: boolean } | null>(null);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const rows = list.data?.appeals ?? [];

  const close = () => {
    setTarget(null);
    setReply('');
    setErr(null);
  };

  const resolve = async () => {
    if (!target) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ status: 'accepted' | 'rejected'; unbanned: boolean }>(`${base}/appeals/${target.appeal.id}/resolve`, {
        method: 'POST',
        body: resolveBody(target.accept, reply)
      });
      // 受け入れても、同じプレイヤーに別の有効な BAN が残っていれば解けない（サーバーの unbanned: false）= そのまま伝える
      setNotice(r.status === 'rejected' ? t('appeals.rejected') : r.unbanned ? t('appeals.accepted') : t('appeals.acceptedStillBanned'));
      close();
      list.reload();
      // プレイヤーの一覧の「BAN 中」の表示を更新させる
      onResolved();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card appeals" aria-labelledby="appeals-title">
      <div className="card-head">
        <h2 className="card-title" id="appeals-title">
          {t('appeals.title')}
        </h2>
        {/* まだ読んでいないページがあるときは「◯件以上」（読んだ分だけを総数のように見せない） */}
        {rows.length > 0 && <span className="pill pill-danger">{t(list.more.has ? 'appeals.openCountMore' : 'appeals.openCount', { count: rows.length })}</span>}
      </div>
      <p className="muted small">{tr('appeals.lead', { fn: <code>kumo.sanctions.appeal()</code> })}</p>
      {notice && (
        <p className="small" role="status">
          {notice}
        </p>
      )}
      {list.loading && !list.data && <Spinner />}
      {list.error && <ErrorState error={list.error} what={t('appeals.what')} onRetry={list.reload} />}
      {list.data && rows.length === 0 && <p className="muted small">{t('appeals.none')}</p>}
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('appeals.col.player')}</th>
                <th scope="col">{t('appeals.col.ban')}</th>
                <th scope="col">{t('appeals.col.message')}</th>
                <th scope="col" className="hide-sm">{t('appeals.col.received')}</th>
                <th scope="col">
                  <span className="sr-only">{t('appeals.accept')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id}>
                  <td>
                    <button type="button" className="link-btn mono" onClick={() => onOpenPlayer(a.playerId)}>
                      {shortId(a.playerId)}
                    </button>
                  </td>
                  <td>
                    <span className="pill pill-danger">{t(sanctionKindKey(a.kind))}</span>
                    {/* BAN の理由は運営の内部メモ（プレイヤーには見えない）。判断の材料として並べる */}
                    <div className="muted small">{t('appeals.yourReason', { reason: a.reason })}</div>
                  </td>
                  <td className="appeal-message">{a.message}</td>
                  <td className="hide-sm" title={fullDate(a.createdAt)}>
                    {ago(a.createdAt)}
                  </td>
                  <td>
                    <div className="row-actions">
                      <button type="button" className="btn btn-primary btn-sm" onClick={() => setTarget({ appeal: a, accept: true })}>
                        {t('appeals.accept')}
                      </button>
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setTarget({ appeal: a, accept: false })}>
                        {t('appeals.reject')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMore more={list.more} />

      <ConfirmDialog
        open={target !== null}
        title={t(target?.accept ? 'appeals.acceptTitle' : 'appeals.rejectTitle')}
        confirmLabel={t(target?.accept ? 'appeals.accept' : 'appeals.reject')}
        busy={busy}
        onClose={close}
        onConfirm={() => void resolve()}
      >
        <p>{t(target?.accept ? 'appeals.acceptBody' : 'appeals.rejectBody')}</p>
        {target && <blockquote className="appeal-quote">{target.appeal.message}</blockquote>}
        <label className="field">
          <span>
            {t('appeals.response')} <small>{t('appeals.responseNote')}</small>
          </span>
          <textarea className="textarea" rows={3} value={reply} onChange={(e) => setReply(e.target.value)} maxLength={APPEAL_RESPONSE_MAX} placeholder={t('appeals.responsePlaceholder')} />
        </label>
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}
